import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';

// MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

// MUI Icons
import DownloadIcon from '@mui/icons-material/Download';

import PPClanciPDF from './PPClanciPDF';

import { CardActionButton } from '@/ui/Card';
import { PPDocument } from '@/lib/prostorniPlanoviHelper';
import useApi from '@/lib/api/useApi';
import useDocuments from '@/components/useDocuments';
import LoaderContext from '@/context/LoaderContext/LoaderContext';

type PPInfoPaneActionsProps = {
  data: PPDocument;
  planId: number;
  clickPosition: string[];
  planName: string;
};

const getFormattedTimestamp = (): string => {
  const date = new Date();

  // Shift to GMT+2
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const gmtPlus2 = new Date(utc + 2 * 60 * 60000);

  const day = gmtPlus2.getDate();
  const month = gmtPlus2.getMonth() + 1; // months are 0-based
  const year = gmtPlus2.getFullYear();

  const hours = gmtPlus2.getHours();
  const minutes = gmtPlus2.getMinutes();
  const seconds = gmtPlus2.getSeconds();

  return `${day}/${month}/${year}-${hours}:${minutes}:${seconds}`;
};

const PPInfoPaneActions = (props: PPInfoPaneActionsProps) => {
  const { t } = useTranslation();
  const apiInstance = useApi();
  const loaderContext = useContext(LoaderContext);
  const { saveDocument } = useDocuments();

  const { data, planId, planName, clickPosition } = props;

  const handleDownloadAll = () => {
    loaderContext?.toggleLoading(true);
    apiInstance
      .downloadWithMetadata(`prostorni-planovi/${planId}/doc?content=true`)
      .then((resp) => {
        if (resp.success && resp.data) {
          const file = resp.data;

          if (!file.content) {
            console.error('File has no content');
          } else {
            saveDocument(file);
          }
        }
      })
      .finally(() => {
        loaderContext?.toggleLoading(false);
      });
  };

  const handleDownloadCurrent = async () => {
    try {
      loaderContext?.toggleLoading(true);

      const fileName = `${planName.trim().replaceAll(' ', '_')}-isjecak-${getFormattedTimestamp()}.pdf`;
      const doc = <PPClanciPDF planName={planName} clickPosition={clickPosition} data={data} />;
      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      loaderContext?.toggleLoading(false);
    }
  };

  return (
    <Stack height="36px" spacing={1}>
      <Divider />
      <Box width="100%" height="100%" display="flex" justifyContent="space-around" gap={1}>
        <CardActionButton
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{ ml: 0, p: '4px 8px', fontSize: '0.8rem'}}
          onClick={handleDownloadCurrent}
        >
          {t('prostorni_planovi:buttons.export_pdf_current')}
        </CardActionButton>

        <CardActionButton startIcon={<DownloadIcon />} onClick={handleDownloadAll} variant="contained" sx={{ ml: 0, p: '4px 8px', fontSize: '0.8rem' }}>
          {t('prostorni_planovi:buttons.export_all')}
        </CardActionButton>
      </Box>
    </Stack>
  );
};

export default PPInfoPaneActions;
